import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import "../css/AddSubscription.css";
import PageTitle from "./PageTitleCompoent";
import api from "../api"; // centralized axios instance
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: "spring", stiffness: 100 },
  },
};

// Duration options
const durations = [
  { label: "Monthly", value: "monthly" },
  { label: "Quarterly", value: "quarterly" },
  { label: "Half Yearly", value: "half_yearly" },
  { label: "Yearly", value: "yearly" },
];

export default function AddSubscribtion() {
  const navigate = useNavigate();
  const location = useLocation();
  const plan = location.state?.plan;

  const [duration, setDuration] = useState("");
  const [amount, setAmount] = useState("");
  const [services, setServices] = useState("");
  const [usedDurations, setUsedDurations] = useState([]);

  // ✅ Load existing plan data if editing
  useEffect(() => {
    if (plan) {
      const normalized = plan.subscription_duration
        ?.trim()
        .toLowerCase()
        .replace(" ", "_");
      setDuration(normalized || "");
      setAmount(plan.subscription_amount?.toString() || "");
      setServices(plan.services || "");
    }
  }, [plan]);

  // ✅ Fetch all subscription plans
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await api.get("/subscription-plans/");
        let durationsInUse = [];

        if (Array.isArray(res.data.subscription_plans)) {
          durationsInUse = res.data.subscription_plans.map(
            (p) =>
              p.subscription_duration?.trim().toLowerCase().replace(" ", "_")
          );
        } else if (Array.isArray(res.data)) {
          durationsInUse = res.data.map(
            (p) =>
              p.subscription_duration?.trim().toLowerCase().replace(" ", "_")
          );
        }

        localStorage.setItem(
          "usedDurations",
          JSON.stringify(durationsInUse.filter(Boolean))
        );

        setUsedDurations(durationsInUse.filter(Boolean));
      } catch (err) {
        console.error("Failed to fetch subscription plans:", err);
        const saved = localStorage.getItem("usedDurations");
        if (saved) setUsedDurations(JSON.parse(saved));
      }
    };

    fetchPlans();
  }, []);

  const handleBack = () => {
    navigate("/subscription-plan");
  };

  const handleSave = async () => {
    if (!duration || !amount || !services) {
      toast.warn("Please fill in all fields including selecting a duration.");
      return;
    }

    const formData = {
      subscription_duration: duration,
      subscription_amount: amount.toString(),
      services: services.trim(),
    };

    try {
      if (plan?.id) {
        // Update existing plan
        await api.put(`/subscription-plans/${plan.id}/`, formData);
        toast.success("Subscription plan updated successfully!");
      } else {
        // Create new plan
        await api.post(`/subscription-plans/add/`, formData);
        toast.success("Subscription plan added successfully!");
      }

      // ✅ Update used durations in localStorage
      const saved = localStorage.getItem("usedDurations");
      let updatedDurations = saved ? JSON.parse(saved) : [];
      if (!updatedDurations.includes(duration)) {
        updatedDurations.push(duration);
      }
      localStorage.setItem("usedDurations", JSON.stringify(updatedDurations));

      // ✅ Redirect after a short delay
      setTimeout(() => {
        navigate("/subscription-plan");
      }, 1500);
    } catch (err) {
      console.error("Failed to save subscription plan:", err);
      toast.error("Failed to save subscription plan. Please try again.");
    }
  };

  return (
    <div className="Add-subscription-container">
      <Sidebar />
      <motion.div
        className="Add-subscription-main"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <Header />
        <div className="Add-subscription-content">
          <motion.div variants={itemVariants} className="Add-subscription-row">
            <PageTitle
              title={plan ? "Edit Subscription Plan" : "Add Subscription Plan"}
              onBack={handleBack}
            />
          </motion.div>
        </div>

        {/* Form Row 1 */}
        <motion.div variants={itemVariants} className="Add-subscription-grid">
          <div className="Add-subscription-form-row">
            {/* Duration Dropdown */}
            <div className="Add-subscription-form-group1">
              <label className="AddSubscriptionlabel" htmlFor="duration">
                <h3 className="Add-subscription-text2">
                  Subscription Duration
                </h3>
              </label>
              <select
                className="Add-subscription-inputbox"
                id="duration"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                style={{ width: "65%" }}
              >
                <option value="">Select Duration</option>
                {durations.map(({ label, value }) => {
                  const isUsed = usedDurations.includes(value);
                  const isCurrent =
                    plan?.subscription_duration
                      ?.trim()
                      .toLowerCase()
                      .replace(" ", "_") === value;

                  return (
                    <option
                      key={value}
                      value={value}
                      disabled={isUsed && !isCurrent}
                    >
                      {label}
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Amount */}
            <div className="Add-subscription-form-group1">
              <label className="AddSubscriptionlabel" htmlFor="amount">
                <h3 className="Add-subscription-text2">
                  Subscription Amount
                </h3>
              </label>
              <input
                className="Add-subscription-inputbox"
                type="number"
                id="amount"
                placeholder="Enter Amount..."
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                style={{ width: "65%" }}
              />
            </div>
          </div>
        </motion.div>

        {/* Services */}
        <motion.div variants={itemVariants} className="Add-subscription-num">
          <div className="Add-subscription-form-row">
            <div className="Add-subscription-form-group3">
              <label className="Add-Subscription-label" htmlFor="services">
                <h3 className="Add-subscription-text3">Services</h3>
              </label>
              <textarea
                className="Add-subscription-inputbox1"
                id="services"
                placeholder="Enter services, comma-separated..."
                value={services}
                onChange={(e) => setServices(e.target.value)}
                style={{ width: "90%", height: "100px" }}
              />
              <div className="Add-subscription-button-wrapper">
                <motion.button
                  className="Add-subscription-save-button"
                  onClick={handleSave}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 400, damping: 17 }}
                >
                  <span className="Add-subscription-save-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width={24}
                      height={24}
                      viewBox="0 0 24 24"
                    >
                      <path
                        fill="currentColor"
                        d="M19 13v5c0 .55-.45 1-1 1H6c-.55 0-1-.45-1-1v-5c0-.55-.45-1-1-1s-1 .45-1 1v6c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-6c0-.55-.45-1-1-1s-1 .45-1 1m-6-.33l1.88-1.88a.996.996 0 1 1 1.41 1.41l-3.59 3.59a.996.996 0 0 1-1.41 0L7.7 12.2a.996.996 0 1 1 1.41-1.41L11 12.67V4c0-.55.45-1 1-1s1 .45 1 1z"
                      />
                    </svg>
                  </span>
                  <span className="Add-subscription-save-text">
                    {plan ? "Update" : "Save"}
                  </span>
                </motion.button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Toast Container */}
        <ToastContainer position="top-right" autoClose={3000} />
      </motion.div>
    </div>
  );
}
